import {
  WIZARD_STEPS,
  type ResearchState,
  type OnboardingState,
  type SetupProgressDto,
  type SetupStatus,
  type StepGuideDto,
  type WizardMoveInput,
  type WizardStepDto,
  type WizardStepKey,
} from '@bk/contracts';
import { ownerError } from '@bk/contracts';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { navigate } from '../../../lib/router';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, Card, ErrorText, Empty } from '../../../ui';
import { ActivityLoader, MessageBubble } from '../Onboarding';
import { Discoveries } from '../../Discoveries';
import { useSetupProgress } from '../Setup';
import { CompanyStep } from './CompanyStep';
import { SitesStep } from './SitesStep';
import { OrganizationStep } from './OrganizationStep';
import { ActivitiesStep } from './ActivitiesStep';
import { ProceduresStep } from './ProceduresStep';
import { ToolsStep } from './ToolsStep';
import { isJudgedStep, StepVerdictBar, stepOpenKey } from './StepReview';
import { TalkProvider } from './Talk';
import { IdealDesign } from './Ideal';
import { StepIntro } from './Illustration';
import { EquipmentStep } from './Equipment';
import { CompanyPlanPage } from './CompanyPlan';
import { FinaleView } from './Finale';
import { AlTimone } from './AlTimone';
import { Obblighi } from './Obblighi';
import { Contratto } from './Contratto';
import { ConsultantStep, shortName, useStepFailure } from './Consulente';
import { PhaseCommandBar, PhaseCommands, PhaseCommandsProvider, PrevPhaseButton } from './Comandi';
import { StepTimeline } from './StepTimeline';
import { Fase0Page } from '../../sinapsi/Fase0';
import { PHASE_PAGES } from '../../sinapsi/fasi-pagine';
import { Registry } from '../../sinapsi/Fase';
import { PhaseNav, SinapsiPage } from '../../sinapsi/Sinapsi';
import { PHASES, type CompanyCardDto, type PhaseCommonDto, type PhaseQuestionsDto, type PhaseZeroDto } from '@bk/contracts';
import './phases.css';

/** «La tua azienda» (ADR 0042): il risultato della configurazione, dopo l'ultima fase. */
const AZIENDA = 'la-tua-azienda';
const TIMONE = 'timone';

/**
 * Configurazione guidata a fasi (ADR 0077): la pagina mostra solo la fase in cui si è, con in alto il cerchio
 * dell'avanzamento. Non si torna indietro: «Avanti» sigilla la fase (il server ne salva la fotografia, ADR 0072) e un
 * indirizzo di una fase già chiusa apre quella raggiunta; per cambiare una fase chiusa si scrive al consulente.
 */
export function WizardPage({ step }: { step?: string }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const progress = useSetupProgress();
  const p = progress.data;
  const steps = p?.wizard ?? [];
  // La fase raggiunta con «Avanti». Un passo può sparire (con la visura «L'impresa» non si mostra): vale l'ultimo
  // passo visibile prima di lui.
  const order = (k: string | undefined) => (WIZARD_STEPS as readonly string[]).indexOf(k ?? '');
  const reached = steps.reduce(
    (n, s, k) => (order(s.chiave) <= order(p?.guidata?.raggiunto) ? k : n),
    0,
  );
  // Si è sempre nella fase raggiunta: le fasi chiuse non si riaprono, quelle dopo non si aprono ancora.
  const current = steps[reached];
  const go = (k: WizardStepKey) => navigate(`/t/${tenant.id}/configurazione/${k}`);
  // Finiti i passi (ADR 0040) resta solo «La tua azienda».
  const result = !!p?.guidata.finita;
  const azienda = () => navigate(`/t/${tenant.id}/configurazione/${AZIENDA}`);
  // L'indirizzo segue la fase in cui si è: un passo chiuso o non ancora aperto nell'indirizzo porta alla fase raggiunta.
  // «timone» è «La tua azienda» aperta direttamente su «Al timone» (dall'elenco delle aziende, ADR 0173).
  const alTimone = step === TIMONE && result;
  useEffect(() => {
    if (!p || !current || alTimone) return;
    const here = result ? AZIENDA : current.chiave;
    if (step && step !== here) window.location.replace(`#/t/${tenant.id}/configurazione/${here}`);
  }, [p, current, result, step, alTimone, tenant.id]);
  // Arrivati con «Al timone»: si scorre fino alla pagina appena è montata (i suoi dati arrivano dopo il primo render).
  useEffect(() => {
    if (!alTimone) return;
    let tries = 0;
    const id = window.setInterval(() => {
      const el = document.getElementById('al-timone');
      if (el) el.scrollIntoView({ block: 'start' });
      if (el || ++tries > 40) window.clearInterval(id);
    }, 150);
    return () => window.clearInterval(id);
  }, [alTimone]);
  // La fase appena sigillata, per il momento breve in cui il cerchio cresce. Cambiare fase rimonta la pagina (la
  // chiave è l'indirizzo): il sigillo resta in memoria per la pagina nuova.
  const [sealed, setSealed] = useState(() =>
    lastSeal && Date.now() - lastSeal.at < SEAL_MS ? lastSeal : null,
  );
  useEffect(() => {
    if (!sealed) return;
    const timer = setTimeout(() => setSealed(null), SEAL_MS - (Date.now() - sealed.at));
    return () => clearTimeout(timer);
  }, [sealed]);
  const sealDone = (titolo: string) => {
    lastSeal = { titolo, da: p?.percentuale_guidata ?? 0, at: Date.now() };
    setSealed(lastSeal);
  };
  const move = useMutation({
    mutationFn: (body: WizardMoveInput) =>
      api<SetupProgressDto['guidata']>(t('/onboarding/wizard'), { body }),
    onSuccess: (guidata) => {
      qc.setQueryData<SetupProgressDto>(['tenant', tenant.id, 'onboarding-progress'], (old) =>
        old ? { ...old, guidata } : old,
      );
      // La percentuale la calcola il server: si rilegge il percorso.
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'onboarding-progress'] });
    },
  });
  // «Avanti» sigilla la fase: il giudizio «giusto» fa salvare la fotografia del passo (ADR 0072). Chi aveva scelto
  // «Può andar bene» lo tiene: anche quello salva la fotografia, ma lascia da verificare ciò che è supposto.
  const seal = (s: WizardStepDto) => ({
    passo: s.chiave,
    valore: p?.guidata.esiti?.[s.chiave] === 'va_bene' ? ('va_bene' as const) : ('giusto' as const),
  });
  const advance = (k: WizardStepKey) =>
    current &&
    move.mutate(
      { raggiunto: k, esito: seal(current) },
      {
        onSuccess: () => {
          sealDone(current.titolo);
          go(k);
        },
      },
    );
  // Dall'ultima fase: la configurazione guidata è finita e si va a «La tua azienda».
  const finish = () =>
    current &&
    move.mutate(
      { finita: true, esito: seal(current) },
      {
        onSuccess: () => {
          sealDone(current.titolo);
          azienda();
        },
      },
    );
  const i = current ? steps.indexOf(current) : -1;
  // I comandi del titolare sul percorso (ADR 0172): la fase prima (da «La tua azienda» l'ultima), fermare o riprendere
  // la fase aperta, uscire dal contratto. Tornare riapre la fase: il server toglie il suo giudizio e la riapre.
  const prev = result ? (steps.at(-1) ?? null) : i > 0 ? (steps[i - 1] ?? null) : null;
  const working = !!current && !result && (current.lavori.length > 0 || (!!current.tempi && !current.tempi.fine));
  const stopped = !!current && !result && !!current.fermato;
  const back = () =>
    prev &&
    move.mutate(
      { torna: prev.chiave },
      {
        onSuccess: () => go(prev.chiave),
      },
    );
  const stepCommand = useMutation({
    mutationFn: ({ k, cosa }: { k: WizardStepKey; cosa: 'ferma' | 'riprendi' }) =>
      api(t(`/onboarding/guide/${k}/${cosa}`), { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', tenant.id] }),
  });
  const exit = () =>
    move.mutate(
      { uscita: true },
      {
        onSuccess: () => navigate(`/t/${tenant.id}/oggi`),
      },
    );
  const commands =
    p && current
      ? {
          prev,
          working,
          stopped,
          pending: move.isPending || stepCommand.isPending,
          back,
          stop: () => stepCommand.mutate({ k: current.chiave, cosa: 'ferma' }),
          resume: () => stepCommand.mutate({ k: current.chiave, cosa: 'riprendi' }),
          exit,
        }
      : null;
  // La cornice Sinapsi (ADR 0171): il nome dell'azienda nella testata, le domande aperte dell'incarico, il livello della fase 0.
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company');
  const domande = useTenantQuery<PhaseQuestionsDto>(['domande'], '/onboarding/domande', { refetchInterval: 15_000 });
  const fase0 = useTenantQuery<PhaseZeroDto>(['fase0'], '/onboarding/fase0', { enabled: !!current && current.chiave !== 'visura' });
  const nomeAzienda = shortName(card.data?.identita.ragione_sociale ?? null) ?? tenant.name;
  const aperte = domande.data?.registro.aperte ?? 0;
  const scelte = domande.data?.registro.scelte_timonio ?? 0;
  const at = current ? (WIZARD_STEPS as readonly string[]).indexOf(current.chiave) : -1;
  // Il livello delle fasi già raggiunte con la pagina del servizio delle fasi (art. 9.3): lo calcola il server.
  const pagedBefore = PHASES.filter((x) => x.n > 0 && x.passo && PHASE_PAGES[x.passo] && (WIZARD_STEPS as readonly string[]).indexOf(x.passo) < at);
  const phaseLevels = useQueries({
    queries: pagedBefore.map((x) => ({ queryKey: ['tenant', tenant.id, 'fase', x.n], queryFn: () => api<PhaseCommonDto>(t(`/onboarding/fase/${x.n}`)) })),
  });
  const lv = (d?: { livello: { valore: number } | null } | null) => (d?.livello ? `${d.livello.valore}/5` : undefined);
  const livelli: Partial<Record<number, string>> = { 0: lv(fase0.data), ...Object.fromEntries(pagedBefore.map((x, k) => [x.n, lv(phaseLevels[k]?.data)])) };
  const faseN = current ? (PHASES.find((x) => x.passo === current.chiave)?.n ?? null) : null;
  const perFase = (domande.data?.registro.per_fase ?? [])
    .filter((x) => x.aperte)
    .map((x) => `${x.aperte} ${x.fase === faseN ? 'di questa fase' : `della fase ${x.fase}`}`)
    .join(', ');
  const [registry, setRegistry] = useState(false);
  // Dalla Visura il cervello comincia a preparare i passi dopo, mentre si legge l'atto (ADR 0146).
  const atVisura = current?.chiave === 'visura';
  useEffect(() => {
    if (atVisura)
      void api(t('/onboarding/guide/visura/prepare'), { method: 'POST' }).catch(() => undefined);
  }, [atVisura]); // eslint-disable-line react-hooks/exhaustive-deps
  // Una fase nuova si legge dall'inizio (graffe necessarie: scrollTo può restituire una Promise).
  useEffect(() => {
    void window.scrollTo({ top: 0 });
  }, [step]);
  // Uscendo dalla configurazione il passo lasciato si riprogetta con le scelte fatte (ADR 0069).
  useEffect(
    () => () => {
      void api(t('/onboarding/guide-close'), { method: 'POST' }).catch(() => undefined);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return (
    <PhaseCommandsProvider value={commands}>
      <SinapsiPage
        glow="d"
        network={`Rete neurale di ${nomeAzienda}`}
        state={
          result ? (
            'La tua azienda'
          ) : aperte > 0 || scelte > 0 ? (
            <>
              {aperte} {aperte === 1 ? 'domanda aperta' : 'domande aperte'}
              <span style={{ marginLeft: 18, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span className="sn-dot" style={{ width: 8, height: 8, border: '1px solid #ffd68c', background: 'transparent', boxShadow: 'none' }} />
                {scelte} {scelte === 1 ? 'scelta di Timonio' : 'scelte di Timonio'}
              </span>
            </>
          ) : faseN !== null ? (
            `Fase ${faseN} · ${current?.titolo ?? ''}`
          ) : (
            'Configurazione'
          )
        }
        stateTone={aperte > 0 && !result ? 'gold' : result ? 'ok' : 'on'}
        tools={commands ? <PhaseCommands /> : undefined}
        onExit={{ label: 'Le tue aziende', go: () => navigate('/aziende') }}
      >
      <div className="sn-wrap wide sn-page">
        <ErrorText>{progress.error ? errorMessage(progress.error) : ''}</ErrorText>
        {p && current && (
          <PhaseNav steps={steps} current={result ? null : current.chiave} levels={livelli} open={aperte} chosen={scelte} perPhase={perFase || null} onRegistry={() => setRegistry(true)} />
        )}
        {registry && <Registry onClose={() => setRegistry(false)} />}
        {p && current && result && (
          // Finita la configurazione: prima l'azienda come una pagina del suo sito (ADR 0135), poi «Al timone» con l'acquisto
          // (contratto, fase 13; ADR 0162), poi il dettaglio del contratto e il piano per arrivarci.
          <div key={AZIENDA} className="bk-phase-in sn-main">
            <FinaleView />
            <AlTimone />
            <Contratto />
            <Obblighi />
            <CompanyPlanPage />
          </div>
        )}
        {p && current && !result && current.chiave === 'visura' && (
          <Fase0Page
            next={i < steps.length - 1 ? steps[i + 1]! : null}
            onNext={() => advance(steps[i + 1]!.chiave)}
            sealing={move.isPending}
            stopped={stopped}
            onWithout={() => {
              const k = steps[i + 1]!.chiave;
              move.mutate({ raggiunto: k, senza_visura: true }, { onSuccess: () => go(k) });
            }}
          />
        )}
        {p && current && !result && !!PHASE_PAGES[current.chiave] && (
          // Le fasi 1-3 del contratto 2.0 (ADR 0174): pagine proprie, preparate dallo studio e dal consulente.
          <div className="sn-main" key={current.chiave}>
            <ErrorText>{move.error ? errorMessage(move.error) : ''}</ErrorText>
            {(() => {
              const nextStep = i < steps.length - 1 ? steps[i + 1]! : null;
              const props = { next: nextStep, onNext: () => (nextStep ? advance(nextStep.chiave) : finish()), sealing: move.isPending, stopped };
              const Page = PHASE_PAGES[current.chiave]!;
              return <Page {...props} />;
            })()}
          </div>
        )}
        {p && current && !result && current.chiave !== 'visura' && !PHASE_PAGES[current.chiave] && (
          <div className="bkc sn-main" key={current.chiave}>
            <ErrorText>{move.error ? errorMessage(move.error) : ''}</ErrorText>
            <div className="bk-phase-in min-w-0 space-y-4">
              {!OWN_LAYOUT.includes(current.chiave) && !CONSULTANT_STEPS.includes(current.chiave) && <StepHeader s={current} />}
              <StepBody
                key={current.chiave}
                s={current}
                next={i < steps.length - 1 ? steps[i + 1]! : null}
                go={advance}
                finish={i === steps.length - 1 ? finish : null}
                sealing={move.isPending}
              />
            </div>
          </div>
        )}
      </div>
      </SinapsiPage>
    </PhaseCommandsProvider>
  );
}

/** Quanto dura il momento del sigillo. */
const SEAL_MS = 2800;
/** L'ultima fase sigillata, con la percentuale di prima: sopravvive al cambio di pagina. */
let lastSeal: { titolo: string; da: number; at: number } | null = null;

/** Il cerchio dell'avanzamento: raggio e circonferenza del tratto nel riquadro 80×80. */
const RING_R = 32;
const RING_C = 2 * Math.PI * RING_R;

/**
 * In alto, sempre uguale: il cerchio con la percentuale, la fase in cui si è e la regola del percorso. Quando una fase
 * si sigilla il cerchio cresce e per un attimo dice quale fase è chiusa (senza movimento per chi lo ha chiesto).
 */
export function PhaseHeader({
  percent,
  eyebrow,
  title,
  text,
  steps,
  at,
  sealed,
  from,
  done,
}: {
  percent: number;
  eyebrow: string;
  title: string;
  /** Cosa si fa in questa fase, in una frase. */
  text: string | null;
  /** Le fasi del percorso, per i tasselli: chiuse, quella in corso, quelle dopo. */
  steps: WizardStepDto[];
  /** La fase in cui si è (indice in `steps`); `steps.length` a configurazione finita. */
  at: number;
  sealed: string | null;
  /** La percentuale prima del sigillo: il cerchio parte da qui e cresce. */
  from: number | null;
  done: boolean;
}) {
  const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
  const pct = clamp(percent);
  const [shown, setShown] = useState(() => clamp(from ?? percent));
  useEffect(() => {
    // Un fotogramma dopo, così il tratto parte dal valore di prima e la transizione si vede.
    const frame = requestAnimationFrame(() => setShown(pct));
    return () => cancelAnimationFrame(frame);
  }, [pct]);
  // Lo stesso pannello di «La tua azienda» (ADR 0086): blu notte, testo chiaro, il percorso a tasselli e il cerchio.
  return (
    <header
      className="mb-6 grid overflow-hidden rounded-3xl bg-[#0d1220] text-[#eef2ff] md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]"
      aria-live="polite"
    >
      <div className="flex min-w-0 flex-col gap-4 p-6 md:p-7">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-white/55">{eyebrow}</p>
          <h1 className="mt-2 text-2xl font-semibold leading-tight text-balance md:text-3xl">
            {title}
          </h1>
          {sealed ? (
            <p className="bk-sealed mt-2 text-sm font-medium text-emerald-300">
              ✓ «{sealed}» è chiusa: ora è la base delle fasi dopo.
            </p>
          ) : (
            text && <p className="mt-2 max-w-prose text-sm text-white/75">{text}</p>
          )}
        </div>
        <ol
          className="grid grid-cols-4 gap-1.5 sm:grid-cols-8"
          aria-label="Le fasi della configurazione"
        >
          {steps.map((x, k) => {
            const closed = k < at;
            const here = k === at;
            return (
              <li
                key={x.chiave}
                aria-current={here ? 'step' : undefined}
                className={`rounded-xl px-2 py-1.5 ring-1 ${here ? 'bg-white/[0.14] ring-white/40' : closed ? 'bg-white/[0.06] ring-white/10' : 'ring-white/[0.06]'}`}
              >
                <span
                  className={`block text-[11px] font-semibold tabular-nums ${closed ? 'text-emerald-300' : here ? 'text-white' : 'text-white/35'}`}
                >
                  {closed ? '✓' : k + 1}
                </span>
                <span
                  className={`block truncate text-[11px] leading-tight ${here ? 'text-white' : closed ? 'text-white/70' : 'text-white/35'}`}
                >
                  {x.titolo}
                </span>
              </li>
            );
          })}
        </ol>
        {!done && !sealed && (
          <p className="text-xs text-white/45">
            Una fase alla volta, senza tornare indietro: per cambiare una fase chiusa scrivilo al
            consulente in «Parliamone».
          </p>
        )}
      </div>
      <div className="flex items-center justify-center border-t border-white/10 p-6 md:border-l md:border-t-0">
        <svg
          viewBox="0 0 80 80"
          className={`h-32 w-32 md:h-40 md:w-40 ${sealed ? 'bk-ring-sealed' : ''}`}
          role="img"
          aria-label={`Avanzamento della configurazione: ${pct}%`}
        >
          <circle
            cx="40"
            cy="40"
            r={RING_R}
            fill="none"
            strokeWidth="6"
            className="stroke-white/10"
          />
          <circle
            cx="40"
            cy="40"
            r={RING_R}
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={RING_C}
            strokeDashoffset={RING_C * (1 - shown / 100)}
            transform="rotate(-90 40 40)"
            className={`bk-ring ${done ? 'stroke-emerald-400' : 'stroke-[#8fb0ff]'}`}
          />
          <text
            x="40"
            y="44"
            textAnchor="middle"
            className="fill-white text-[15px] font-semibold tabular-nums"
          >
            {shown}%
          </text>
          <text x="40" y="55" textAnchor="middle" className="fill-white/50 text-[6px]">
            {done ? 'completata' : 'della configurazione'}
          </text>
        </svg>
      </div>
    </header>
  );
}

/** Passi con una pagina tutta loro, come la visura: niente intestazione, guida, chat né pulsanti comuni. */
const OWN_LAYOUT: readonly WizardStepKey[] = ['impresa'];

/** I passi nella forma delle pagine prototipo (ADR 0146). */
const CONSULTANT_STEPS: readonly WizardStepKey[] = [
  'sedi',
  'attivita',
  'impianti',
  'software',
  'organizzazione',
  'procedure',
];

/**
 * In nessun passo una risposta della persona o un aggiornamento in background fa comparire popup né domande «aggiorno?»
 * (ADR 0046, 0052); finché il passo si rifà c'è il suo loader, non la versione di prima (ADR 0066).
 */
const QUIET: readonly WizardStepKey[] = WIZARD_STEPS;

/** Passi che usano la ricerca online dell'impresa (ADR 0021-0022). */
const RESEARCH_STEPS: readonly WizardStepKey[] = ['impresa', 'sedi', 'attivita', 'organizzazione'];

const guideBusy = (d?: StepGuideDto) =>
  !!d &&
  (d.generating || !!d.ideale?.impresa?.generating || !!d.ideale?.sedi.some((s) => s.generating));

/**
 * La ricerca online dell'impresa: parte da sola la prima volta e, quando finisce, i dati dei passi si aggiornano.
 * Vive nel corpo del passo, così lavora anche mentre il passo è in preparazione.
 */
function useAutoResearch(active: boolean) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const state = useTenantQuery<ResearchState>(['research'], '/onboarding/research', {
    enabled: active,
    refetchInterval: (d) => (d?.in_corso ? 1500 : false),
  });
  const start = useMutation({
    mutationFn: () => api<ResearchState>(t('/onboarding/research'), { method: 'POST' }),
    onSuccess: (r) => qc.setQueryData(['tenant', tenant.id, 'research'], r),
  });
  // Sedi nuove dopo la ricerca (es. aggiunte dalla persona): si cercano online da sole, una per una (ADR 0044).
  const startSites = useMutation({
    mutationFn: () => api<ResearchState>(t('/onboarding/research/sites'), { method: 'POST' }),
    onSuccess: (r) => qc.setQueryData(['tenant', tenant.id, 'research'], r),
  });
  const started = useRef(false);
  const sitesStarted = useRef(false);
  const wasRunning = useRef(false);
  const r = active ? state.data : undefined;
  useEffect(() => {
    if (r && !r.eseguita_il && !r.in_corso && !started.current) {
      started.current = true;
      start.mutate();
    }
    if (r?.eseguita_il && !r.in_corso && r.sedi_da_cercare > 0 && !sitesStarted.current) {
      sitesStarted.current = true;
      startSites.mutate();
    }
    if (wasRunning.current && r && !r.in_corso)
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    wasRunning.current = !!r?.in_corso;
  }, [r]); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    r,
    // Finché la ricerca non ha mai concluso (o non riesce a partire) il passo non è pronto.
    // Solo la prima ricerca blocca il passo; una ricerca rifatta lo aggiorna mentre lo si usa.
    waiting: active && !state.isError && !start.isError && (!r || !r.eseguita_il),
  };
}

/** Lavori del server che scrivono guida e progetto ideale del passo. */
const GUIDE_LABELS = [
  'Raccolgo ciò che ho già trovato e cosa manca',
  'Progetto come dovrebbe essere questa parte dell’azienda',
];

/** Lavori del server già mostrati nell'elenco dal client, con più dettaglio. */
const CLIENT_LABELS = [
  'Cerco la tua azienda online',
  'Raccolgo ciò che ho già trovato e cosa manca',
  'Progetto come dovrebbe essere questa parte dell’azienda',
];

/**
 * Il corpo del passo. Finché c'è un lavoro che lo riempie (ricerca online, lettura dei documenti, guida, progetto
 * ideale, impianti e gli altri) il passo mostra solo cosa il sistema sta facendo e non si può usare; quando ha tutto si
 * costruisce la pagina finita, così non mostra mai dati a metà né avvisi di aggiornamento. Se un lavoro riparte con il
 * passo aperto (un dato nuovo, una risposta in chat) un popup chiede se aggiornare (loader e poi la pagina rifatta) o
 * ignorare (si resta sulla pagina così com'è). La pagina già costruita resta montata ma nascosta durante il loader, così
 * la chat non perde ciò che si stava scrivendo.
 * Nei passi in cui è la persona a dare i dati (impresa, sedi, attività, impianti, organizzazione…) ogni sua risposta fa
 * riscrivere guida e progetto ideale: mai popup né domande «aggiorno?», ma finché il passo si rifà al posto della
 * pagina c'è il loader, perché la versione di prima non si mostra (ADR 0066).
 */
function StepBody({
  s,
  next,
  go,
  finish,
  sealing,
}: {
  s: WizardStepDto;
  next: WizardStepDto | null;
  go: (k: WizardStepKey) => void;
  /** Dopo l'ultimo passo: apre «La tua azienda». */
  finish: (() => void) | null;
  /** «Avanti» sta sigillando la fase. */
  sealing: boolean;
}) {
  const step = s.chiave;
  const progress = useSetupProgress();
  const here = useTenant();
  const client = useQueryClient();
  // Le novità della chat in questo passo (ADR 0052): si mostrano a chi lo apre, che da quel momento le ha viste.
  const [news] = useState(() => s.novita ?? []);
  // Finché il server non ha preso l'apertura, le domande del passo non si mostrano (BrainNotes): potrebbero essere
  // quelle di prima, che il passo riprogettato sostituisce.
  const opening = useMutation({
    mutationKey: stepOpenKey(here.tenant.id, step),
    mutationFn: () => api(here.t(`/onboarding/guide/${step}/open`), { method: 'POST' }),
    onSettled: () => client.invalidateQueries({ queryKey: ['tenant', here.tenant.id] }),
  });
  useEffect(() => {
    void api(here.t('/onboarding/wizard'), { body: { visto: step } })
      .then(() =>
        client.invalidateQueries({ queryKey: ['tenant', here.tenant.id, 'onboarding-progress'] }),
      )
      .catch(() => undefined);
    // Aprire il passo lo riprogetta, con quello appena lasciato (ADR 0069); poi le scelte della persona non lo fanno
    // ripartire finché resta qui.
    opening.mutate();
    // Una volta per apertura del passo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  // L'impresa non ha guida né progetto ideale: aspetta solo la ricerca online e la lettura dei documenti.
  const simple = step === 'impresa';
  const guide = useTenantQuery<StepGuideDto>(['guide', step], `/onboarding/guide/${step}`, {
    enabled: !simple,
    // Anche mentre il passo è in preparazione: a lavori finiti la guida dev'essere quella nuova.
    refetchInterval: (d) => (!d || guideBusy(d) || s.preparando ? 3000 : false),
  });
  const research = useAutoResearch(RESEARCH_STEPS.includes(step));
  const g = guide.data;
  const loading = !simple && !g && !guide.isError;
  const researching = research.waiting || !!research.r?.in_corso;
  const busy = simple
    ? researching || (s.preparando && s.lavori.some((l) => !GUIDE_LABELS.includes(l)))
    : loading || (!guide.isError && (guideBusy(g) || s.preparando || researching));
  // La pagina si costruisce la prima volta a lavori finiti; poi resta montata e si nasconde durante il loader, anche
  // quando il passo si rifà: la versione di prima non si mostra (ADR 0066, rivede l'ADR 0051). Nei passi con un progetto
  // ideale conta il progetto, non la guida: senza progetto la pagina sarebbe vuota.
  const ready = !!g && (g.ideale ? !!g.ideale.aggiornato : !!g.aggiornato);
  const [built, setBuilt] = useState(ready);
  // Lavori ripartiti a pagina costruita: si chiede (ask), si aggiorna (update) o si ignora (ignore) fino al prossimo giro.
  const [round, setRound] = useState<'ask' | 'update' | 'ignore'>('ask');
  useEffect(() => {
    if (!busy || ready) setBuilt(true);
  }, [busy, ready]);
  // Dove i dati li dà la persona, un suo tocco non può chiederle di aggiornare: si lavora in silenzio.
  const quiet = QUIET.includes(step);
  // Ciò che il cervello sta riprogettando non si mostra nella versione di prima (ADR 0066): la pagina resta montata ma
  // nascosta e al suo posto c'è il loader del passo, finché il progetto nuovo è pronto.
  const waiting = busy;
  // Il motore AI è fermo per credito (ADR 0151): nessun passo sta lavorando davvero, e l'attesa non lo finge.
  const stopped = progress.data?.motore_fermo ?? null;
  const asking = busy && built && round === 'ask' && !quiet && !stopped;
  // Ogni giro di lavori ha il suo elenco: parte quando il passo torna occupato.
  const [since, setSince] = useState(() => new Date().toISOString());
  // I lavori visti in questo giro: quando il server non li elenca più sono fatti.
  const [seen, setSeen] = useState<string[]>([]);
  const server = s.preparando ? s.lavori.filter((l) => !CLIENT_LABELS.includes(l)) : [];
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (busy && !wasBusy.current) {
      setSince(new Date().toISOString());
      setSeen([]);
    }
    if (!busy) setRound('ask');
    wasBusy.current = busy;
  }, [busy]);
  useEffect(() => {
    if (server.some((l) => !seen.includes(l))) setSeen((x) => [...new Set([...x, ...server])]);
  }, [server.join('|'), seen]); // eslint-disable-line react-hooks/exhaustive-deps
  const order = progress.data?.wizard ?? [];
  const n = order.findIndex((w) => w.chiave === step) + 1;
  // Un passo non riuscito (ADR 0151) non si giudica e il pulsante principale lo rifà; si può andare avanti lo stesso.
  const failure = useStepFailure(step, CONSULTANT_STEPS.includes(step));
  const failed = failure.failed && !waiting;
  const page = CONSULTANT_STEPS.includes(step) ? (
    <ConsultantStep step={step} title={s.titolo} n={n} of={order.length} last={!next} />
  ) : step === 'impresa' ? (
    <CompanyStep next={next} onNext={() => next && go(next.chiave)} />
  ) : step === 'sedi' ? (
    <SitesStep next={next} onNext={() => next && go(next.chiave)} />
  ) : step === 'attivita' ? (
    <ActivitiesStep next={next} onNext={() => next && go(next.chiave)} />
  ) : step === 'organizzazione' ? (
    <OrganizationStep next={next} onNext={() => next && go(next.chiave)} />
  ) : step === 'software' ? (
    <ToolsStep next={next} onNext={() => next && go(next.chiave)} progress={progress.data} />
  ) : step === 'procedure' ? (
    <ProceduresStep next={next} onNext={() => next && go(next.chiave)} finish={finish} />
  ) : (
    <>
      <Guide step={step} onRefresh={() => setRound('update')} />
      <StepContent step={step} />
    </>
  );
  return (
    // «Parliamone» (ADR 0063): un solo posto per parlare con il cervello, uguale su ogni passo.
    <TalkProvider step={step} title={s.titolo} missing={g?.mancano ?? []}>
      {asking && (
        <ChangedDialog
          what={[...(researching ? ['Cerco la tua azienda online'] : []), ...server]}
          onUpdate={() => setRound('update')}
          onIgnore={() => setRound('ignore')}
        />
      )}
      {waiting && stopped && (
        <section role="status" className="rounded-3xl bg-white p-6 ring-1 ring-black/5">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
            Il consulente è fermo
          </p>
          <h2 className="mt-0.5 text-xl font-semibold">Questa fase aspetta il motore AI</h2>
          <p className="mt-1 max-w-prose text-sm text-ink/70">{stopped}</p>
          <p className="mt-2 max-w-prose text-sm text-ink/60">
            Adesso non sto lavorando: riprendo da solo appena il motore risponde di nuovo e la fase
            si apre quando è pronta.
          </p>
          {/* Ciò che il sistema ha già trovato resta vero anche a motore fermo (ADR 0152). */}
          <Discoveries tenantId={here.tenant.id} live={false} className="mt-4" />
          <PhaseCommandBar tone="light" />
        </section>
      )}
      {waiting && !stopped && (
        <section className="rounded-3xl bg-white p-6 ring-1 ring-black/5">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
            Il consulente al lavoro
          </p>
          <h2 className="mt-0.5 text-xl font-semibold">
            {built ? 'Aggiorno questa fase' : 'Preparo questa fase'}
          </h2>
          <p className="mb-4 mt-1 max-w-prose text-sm text-ink/60">
            Raccolgo i dati dai passi precedenti e da tutte le fonti e progetto questa parte della
            tua azienda. Quando ho tutto costruisco il passo e si apre da solo.
          </p>
          {/* Il lettore e il contatore del passo (ADR 0167); prima che il server lo apra, l'elenco dei lavori. */}
          {s.tempi && !s.tempi.fine ? (
            <StepTimeline t={s.tempi} />
          ) : (
            <ActivityLoader
              activity={{
                started_at: since,
                steps: [
                  ...(RESEARCH_STEPS.includes(step) && researching
                    ? [{ label: 'Cerco la tua azienda online', done: false }]
                    : []),
                  ...seen.map((label) => ({ label, done: !server.includes(label) })),
                  ...(simple
                    ? []
                    : [
                        {
                          label: 'Raccolgo ciò che ho già trovato e cosa manca',
                          done: !!g && !g.generating,
                        },
                      ]),
                  ...(g?.ideale?.impresa
                    ? [
                        {
                          label: `${g.ideale.titolo}: l’impresa`,
                          done: !g.ideale.impresa.generating,
                        },
                      ]
                    : []),
                  ...(g?.ideale?.sedi ?? [])
                    .filter((x) => x.generating || x.sede_in_se || x.attivita.length)
                    .map((x) => ({
                      label: `${g!.ideale!.titolo}: ${x.sede}`,
                      done: !x.generating,
                    })),
                ],
              }}
              fallback="Preparo questa fase…"
            />
          )}
          {/* I comandi del titolare dove sta aspettando (ADR 0172): ferma la fase, torna indietro, esci. */}
          <PhaseCommandBar tone="light" />
          {/* Intanto, ciò che il sistema ha già trovato sull'azienda, con la fonte (ADR 0152). */}
          <Discoveries tenantId={here.tenant.id} className="mt-4" />
        </section>
      )}
      {news.length > 0 && !waiting && (
        <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900 ring-1 ring-emerald-200">
          <p className="font-semibold">Novità dall’ultima volta che hai aperto questo passo</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {news.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-emerald-800/70">
            Se qualcosa non va, correggilo qui sotto.
          </p>
        </div>
      )}
      {built && (
        <div className={waiting ? 'hidden' : 'space-y-4'}>
          {page}
          {/* Il giudizio della persona su tutto il passo (ADR 0061). */}
          {isJudgedStep(step) && !failed && <StepVerdictBar step={step} />}
        </div>
      )}
      {/* Impresa, sedi, attività e organizzazione hanno i loro pulsanti, ma finché si preparano servono questi. */}
      {(!OWN_LAYOUT.includes(step) || waiting) && (
        <div className="flex flex-wrap items-center gap-2 pb-6">
          {/* «← Fase precedente» accanto ad «Avanti» (ADR 0172). */}
          {!waiting && <PrevPhaseButton />}
          <span className="flex-1" />
          {!next && finish && (
            <Button disabled={waiting || sealing} onClick={finish}>
              Vedi la tua azienda →
            </Button>
          )}
          {next && failed && (
            <button
              type="button"
              className="px-2 text-sm text-ink/60 underline-offset-2 hover:underline disabled:opacity-50"
              disabled={sealing}
              onClick={() => go(next.chiave)}
            >
              Vai avanti lo stesso
            </button>
          )}
          {next && failed && (
            <Button disabled={failure.redo.isPending} onClick={() => failure.redo.mutate()}>
              {failure.redo.isPending ? 'Lo rifaccio…' : 'Rifai questo passo'}
            </Button>
          )}
          {next && !failed && (
            <Button disabled={waiting || sealing} onClick={() => go(next.chiave)}>
              {waiting ? (
                'Attendi che il passo sia pronto…'
              ) : (
                <>
                  Avanti<span className="hidden sm:inline">: {next.titolo}</span> →
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </TalkProvider>
  );
}

/** Popup al centro: con il passo aperto è cambiato qualcosa che lo riscrive. */
function ChangedDialog({
  what,
  onUpdate,
  onIgnore,
}: {
  what: string[];
  onUpdate: () => void;
  onIgnore: () => void;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onIgnore();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onIgnore]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="step-changed-title"
        className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-xl"
      >
        <h2 id="step-changed-title" className="text-lg font-semibold">
          Qualcosa è cambiato
        </h2>
        <p className="text-sm text-ink/70">
          Sono arrivati dati nuovi e sto rifacendo questo passo
          {what.length ? ':' : '.'}
        </p>
        {what.length > 0 && (
          <ul className="space-y-0.5 pl-4 text-sm text-ink/80">
            {what.map((x) => (
              <li key={x} className="list-disc">
                {x}
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-ink/50">
          «Aggiorna» aspetta che abbia finito e ti mostra il passo rifatto. «Ignora» ti lascia la
          pagina com’è.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onIgnore}>
            Ignora
          </Button>
          <Button autoFocus onClick={onUpdate}>
            Aggiorna
          </Button>
        </div>
      </div>
    </div>
  );
}

function StepHeader({ s }: { s: WizardStepDto }) {
  // Il titolo è già nel pannello della fase: qui solo cosa serve sapere per cominciare (ADR 0086).
  if (!s.dettaglio) return null;
  return (
    <div className="px-1">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">
        Prima di cominciare
      </p>
      <p className="mt-1 max-w-prose text-sm text-ink/65">{s.dettaglio}</p>
    </div>
  );
}

/**
 * La guida del passo (ADR 0024): il progetto ideale di questa parte dell'azienda, impresa → sede → attività, poi cosa
 * ho già trovato e cosa manca. Si aggiorna da sola quando cambiano i dati.
 */
function Guide({ step, onRefresh }: { step: WizardStepKey; onRefresh: () => void }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const busy = guideBusy;
  const guide = useTenantQuery<StepGuideDto>(['guide', step], `/onboarding/guide/${step}`, {
    refetchInterval: (d) => (busy(d) ? 3000 : false),
  });
  const refresh = useMutation({
    mutationFn: () => api<StepGuideDto>(t(`/onboarding/guide/${step}/refresh`), { method: 'POST' }),
    onSuccess: (g) => qc.setQueryData(['tenant', tenant.id, 'guide', step], g),
    // L'ha chiesto l'utente: niente popup, si va dritti al loader.
    onMutate: onRefresh,
  });
  const g = guide.data;
  if (!g)
    return guide.isLoading ? (
      <Card className="p-4 text-sm text-ink/50">Preparo questo passo…</Card>
    ) : null;
  return (
    <div className="space-y-3">
      {g.ideale && (
        <section className="rounded-2xl bg-accent/[0.05] p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="min-w-[14rem] flex-1">
              <h3 className="text-sm font-semibold">{g.ideale.titolo}</h3>
              <p className="text-xs text-ink/50">
                Come dovrebbe essere la tua azienda, progettata da ciò che so di sede, luogo,
                attività e addetti: è il riferimento a cui tendere, non un dato della tua azienda.
              </p>
            </div>
            {!busy(g) && (
              <button
                className="text-xs text-accent hover:underline"
                disabled={refresh.isPending}
                onClick={() => refresh.mutate()}
              >
                Rifai il progetto
              </button>
            )}
          </div>
          <IdealDesign d={g.ideale} />
        </section>
      )}
      {g.error && !g.generating && !g.trovati.length && !g.mancano.length && (
        <p className="text-sm text-red-700">La guida non è riuscita: {ownerError(g.error)}</p>
      )}
      {(g.trovati.length > 0 || g.mancano.length > 0) && (
        <Card className="divide-y divide-ink/5 px-4 py-1">
          <FoldList
            title="Ho già trovato"
            tone="text-emerald-700"
            count={g.trovati.length}
            empty="Ancora nulla per questo passo."
          >
            {g.trovati.map((x) => (
              <li key={x.cosa}>
                ✓ {x.cosa} <span className="text-ink/45">· {x.fonte}</span>
              </li>
            ))}
          </FoldList>
          <FoldList
            title="Manca"
            tone="text-amber-700"
            count={g.mancano.length}
            empty="Nulla di importante."
          >
            {g.mancano.map((x) => (
              <li key={x}>○ {x}</li>
            ))}
          </FoldList>
        </Card>
      )}
    </div>
  );
}

/** Una riga chiusa con il conteggio: si apre al clic e mostra l'elenco in piccolo. */
function FoldList({
  title,
  tone,
  count,
  empty,
  children,
}: {
  title: string;
  tone: string;
  count: number;
  empty: string;
  children: ReactNode;
}) {
  return (
    <details className="group py-2">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm [&::-webkit-details-marker]:hidden">
        <span className="text-xs text-ink/40 transition-transform group-open:rotate-90">▶</span>
        <span className={`font-medium ${tone}`}>{title}</span>
        <span className="text-xs text-ink/45">{count}</span>
      </summary>
      {count ? (
        <ul className="mt-2 space-y-0.5 pl-5 text-xs text-ink/80">{children}</ul>
      ) : (
        <p className="mt-2 pl-5 text-xs text-ink/50">{empty}</p>
      )}
    </details>
  );
}

function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="flex-1 text-sm font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/** I dati del passo, già compilati da tutte le fonti e modificabili. */
function StepContent({ step }: { step: WizardStepKey }) {
  const { tenant } = useTenant();
  const go = (path: string) => navigate(`/t/${tenant.id}/${path}`);
  const more = (label: string, path: string) => (
    <button className="text-xs text-accent hover:underline" onClick={() => go(path)}>
      {label}
    </button>
  );
  switch (step) {
    case 'impresa':
      // Mostrato da CompanyStep.
      return null;
    case 'sedi':
      // Mostrato da SitesStep.
      return null;
    case 'attivita':
      // Mostrato da ActivitiesStep.
      return null;
    case 'organizzazione':
      // Mostrato da OrganizationStep.
      return null;
    case 'impianti':
      // Gli elenchi del modello dei dati non sono hardware: stanno nella pagina Dati (ADR 0031).
      return <EquipmentStep />;
    case 'software':
      // Mostrato da ToolsStep.
      return null;
    case 'procedure':
      // Mostrato da ProceduresStep.
      return null;
  }
}
